import React, { useState, useRef, useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';
/**
 * InfoTooltip — the ONE explanation surface for the app.
 * Content: pass `text` (a short string) for a one-liner, OR `title` + `points`
 * (an array of short strings) for a titled, bulleted card. A point containing
 * " — " gets its lead phrase bolded automatically.
 * Modes: ICON hint (default), BLOCKED trigger, WRAP (wraps an enabled control).
 * The card is PORTALED to <body> and positioned `fixed`, so it escapes any
 * overflow-hidden ancestor and never clips; it flips/clamps to the viewport.
 */
export default function InfoTooltip({
  text,
  title,
  points,
  children,
  side = 'top',
  triggerClassName,
  triggerStyle,
  triggerTestId = 'info-tooltip-trigger',
  contentTestId = 'info-tooltip-content',
  ariaDisabled = false,
  describedById,
  ariaLabel,
  wrap = false,
}) {
  const [show, setShow] = useState(false);
  const ref = useRef(null);
  const tipRef = useRef(null);
  const hoveringRef = useRef(false);
  const pointerRef = useRef(false);
  const tapHandledRef = useRef(false);
  const autoId = useId();
  const tipId = describedById || autoId;
  const [pos, setPos] = useState({ top: -9999, left: -9999, arrowLeft: 0, placement: side, ready: false });
  useEffect(() => {
    if (!show) return undefined;
    const closeIfOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setShow(false);
    };
    const closeOnEscape = (e) => { if (e.key === 'Escape' || e.key === 'Esc') setShow(false); };
    document.addEventListener('mousedown', closeIfOutside);
    document.addEventListener('touchstart', closeIfOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeIfOutside);
      document.removeEventListener('touchstart', closeIfOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [show]);
  useEffect(() => {
    if (!show) { setPos((p) => (p.ready ? { ...p, ready: false } : p)); return undefined; }
    const compute = () => {
      const trigger = ref.current;
      const tip = tipRef.current;
      if (!trigger || !tip) return;
      const t = trigger.getBoundingClientRect();
      const r = tip.getBoundingClientRect();
      const gap = 8, pad = 8;
      const cx = t.left + t.width / 2;
      let placement = side === 'bottom' ? 'bottom' : 'top';
      let top = placement === 'bottom' ? t.bottom + gap : t.top - r.height - gap;
      if (placement === 'top' && top < pad) { placement = 'bottom'; top = t.bottom + gap; }
      else if (placement === 'bottom' && top + r.height > window.innerHeight - pad) { placement = 'top'; top = t.top - r.height - gap; }
      let left = cx - r.width / 2;
      left = Math.max(pad, Math.min(left, window.innerWidth - pad - r.width));
      const arrowLeft = Math.max(10, Math.min(r.width - 10, cx - left));
      setPos({ top, left, arrowLeft, placement, ready: true });
    };
    const raf = requestAnimationFrame(compute);
    window.addEventListener('scroll', compute, true);
    window.addEventListener('resize', compute);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('scroll', compute, true); window.removeEventListener('resize', compute); };
  }, [show, text, title, points, side]);
  const cleanPoints = Array.isArray(points) ? points.filter(Boolean) : [];
  const body = (
    <>
      {title ? <div className="text-[13.5px] font-bold text-[#F0D98A] mb-2 leading-snug">{title}</div> : null}
      {cleanPoints.length > 0 ? (
        <ul className="flex flex-col gap-[7px] list-none m-0 p-0">
          {cleanPoints.map((p, i) => {
            const s = String(p);
            const idx = s.indexOf(' — ');
            const lead = idx > -1 ? s.slice(0, idx) : null;
            const rest = idx > -1 ? s.slice(idx) : s;
            return (
              <li key={i} className="flex gap-2 text-[12.5px] leading-[1.45] text-white/80">
                <span className="w-[5px] h-[5px] rounded-full bg-[#D4AF37] mt-[6px] shrink-0" />
                <span>{lead ? <><b className="text-white font-semibold">{lead}</b>{rest}</> : rest}</span>
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="text-[13px] leading-[1.55] text-white/80">{text}</div>
      )}
    </>
  );
  const isBottom = pos.placement === 'bottom';
  const tooltip = typeof document !== 'undefined' ? createPortal(
    <span
      id={tipId}
      ref={tipRef}
      role="tooltip"
      className={`fixed z-[9999] block w-max max-w-[300px] px-4 py-3 rounded-xl pointer-events-none transition-opacity duration-150 ${show && pos.ready ? 'opacity-100' : 'opacity-0'}`}
      style={{
        top: `${pos.top}px`,
        left: `${pos.left}px`,
        background: 'linear-gradient(180deg, rgba(20,24,38,0.98), rgba(13,16,28,0.98))',
        border: '1px solid rgba(212,175,55,0.22)',
        boxShadow: '0 14px 40px rgba(0,0,0,0.55)',
        backdropFilter: 'blur(16px)',
      }}
      data-testid={contentTestId}
    >
      {body}
      <span
        className="absolute block w-2 h-2"
        style={{
          left: `${pos.arrowLeft}px`,
          transform: 'translateX(-50%) rotate(45deg)',
          [isBottom ? 'bottom' : 'top']: '100%',
          background: 'rgba(16,19,31,0.98)',
          border: '1px solid rgba(212,175,55,0.22)',
          ...(isBottom ? { borderBottom: 'none', borderRight: 'none' } : { borderTop: 'none', borderLeft: 'none' }),
        }}
      />
    </span>,
    document.body
  ) : null;
  if (wrap) {
    const child = React.Children.only(children);
    return (
      <span
        className="relative inline-flex items-center"
        ref={ref}
        onPointerDown={() => { pointerRef.current = true; }}
        onPointerUp={(e) => { if (e.pointerType === 'touch' || e.pointerType === 'pen') setShow(true); }}
        onMouseEnter={() => { hoveringRef.current = true; setShow(true); }}
        onMouseLeave={() => { hoveringRef.current = false; setShow(false); }}
        onFocus={() => { if (!pointerRef.current) setShow(true); }}
        onBlur={() => { pointerRef.current = false; setShow(false); }}
      >
        {React.cloneElement(child, { 'aria-describedby': tipId })}
        {tooltip}
      </span>
    );
  }
  return (
    <span className="relative inline-flex items-center" ref={ref}>
      <button
        type="button"
        onPointerDown={() => { pointerRef.current = true; }}
        onPointerUp={(e) => {
          if (e.pointerType === 'touch' || e.pointerType === 'pen') {
            tapHandledRef.current = true;
            setShow((p) => !p);
          }
        }}
        onClick={(e) => {
          e.stopPropagation();
          if (tapHandledRef.current) { tapHandledRef.current = false; return; }
          if (hoveringRef.current) return;
          setShow((p) => !p);
        }}
        onMouseEnter={() => { hoveringRef.current = true; setShow(true); }}
        onMouseLeave={() => { hoveringRef.current = false; setShow(false); }}
        onFocus={() => { if (!pointerRef.current) setShow(true); }}
        onBlur={() => { pointerRef.current = false; setShow(false); }}
        aria-disabled={ariaDisabled ? 'true' : undefined}
        aria-label={ariaLabel || undefined}
        aria-describedby={tipId}
        className={triggerClassName || 'inline-flex items-center justify-center w-4 h-4 rounded-full text-white/40 hover:text-[#D4AF37] transition-colors'}
        style={triggerStyle}
        data-testid={triggerTestId}
      >
        {children || <Info className="w-3 h-3" />}
      </button>
      {tooltip}
    </span>
  );
}
